import {
  adminPumpVars,
  defaultPumpVars,
  deletePumps,
  ecopetrolPumpVars,
  kocPumpVars,
  restorePumps
} from "@/models/equipment/pumps/predicates";
import type {
  Pumps,
  PumpsQuery,
  Pumps_Insert_Input,
  Pumps_Bool_Exp
} from "gql/api";
import {
  useDeletePumpsMutation,
  usePumpsQuery,
  useRestorePumpsMutation,
  useInsertPumpMutation,
  useUpdatePumpByPkMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed, ref } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import { pumpTableColumns, type PumpsRow } from "./pumpTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";
import { MIN_CLEARANCE } from "@/composables/validation/validatorConstants";

export interface UsePumpsOptions {
  flowRateBBLD?: number;
  enableFlowRateFilter?: boolean;
  pumpHz?: number;
  topCasingDriftInches?: number;
  bottomCasingDriftInches?: number;
  pumpDepth?: number;
  topCasingDepth?: number;
  usingCompressionPumps?: boolean;
}

export function usePumps(isAdmin: boolean, options: UsePumpsOptions = {}) {
  const baseEquipment = useEquipment<PumpsRow>(isAdmin);
  const title = "Pumps";
  const user = useUserInfo();

  // Reactive state for flow rate filtering
  const flowRateFilter = ref(options.enableFlowRateFilter ?? false);
  const targetFlowRate = ref(options.flowRateBBLD ?? 0);
  const pumpHz = ref(options.pumpHz ?? 60);
  const topCasingDriftInches = ref(options.topCasingDriftInches ?? 0);
  const bottomCasingDriftInches = ref(options.bottomCasingDriftInches ?? 0);
  const pumpDepth = ref(options.pumpDepth ?? 0);
  const topCasingDepth = ref(options.topCasingDepth ?? 0);
  const usingCompressionPumps = ref(options.usingCompressionPumps ?? false);
  const getVariables = () => {
    const baseVars = (() => {
      if (user.is_koc_customer) return kocPumpVars;
      if (user.is_ecopetrol_customer) return ecopetrolPumpVars;
      if (user.is_admin) return adminPumpVars;
      return defaultPumpVars;
    })();

    // Build filter conditions: ROR range, then BEP, then OD
    const conditions: Pumps_Bool_Exp[] = [];

    // Always request housings so pump search can auto-add housings when a pump is selected
    const withHousings = { ...baseVars, include_housings: true };

    let filterCompression: Pumps_Bool_Exp = {};

    // Always check for compression. Only return pumps whose construction is "Comp"
    if (usingCompressionPumps.value) {
      filterCompression = { construction: { _eq: "Comp" } };
      conditions.push(filterCompression);
      withHousings.where = withHousings.where
        ? { _and: [withHousings.where, filterCompression] }
        : filterCompression;
    }

    if (!flowRateFilter.value || !targetFlowRate.value) {
      return withHousings;
    }

    // Calculate frequency adjustment ratio
    // Standard rates are at 60Hz, adjust to target Hz
    const frequencyRatio = pumpHz.value / 60;

    // Adjust target flow rate back to 60Hz equivalent for filtering
    const targetFlowRate60Hz = targetFlowRate.value / frequencyRatio;

    // Calculate ROR range (90-110% of target)
    const minFlowRate = Math.floor(targetFlowRate60Hz * 0.9);
    const maxFlowRate = Math.ceil(targetFlowRate60Hz * 1.1);

    // Filter 1: Frequency-adjusted min/max rate (ROR range)
    // A pump matches if its max rate >= minFlowRate AND its min rate <= maxFlowRate
    // This ensures the pump's operating range overlaps with our target range
    conditions.push({
      max_rate_standard_pump_bpd: { _gte: minFlowRate },
      min_rate_standard_pump_bpd: { _lte: maxFlowRate },
      manufacturer: { _ilike: "%Summit%" }
    });

    // Filter 2: BEP rate adjusted by frequency (will be applied in allRows)
    // Filter 3: OD filtering (will be applied in allRows)

    const flowRateCondition: Pumps_Bool_Exp =
      conditions.length === 1 ? conditions[0] : { _and: conditions };

    if (!baseVars.where) {
      return {
        ...withHousings,
        where: flowRateCondition
      };
    }

    // Combine both filters
    return {
      ...withHousings,
      where: {
        _and: [baseVars.where, flowRateCondition]
      }
    };
  };
  // make reactive
  const queryVariables = computed(() => getVariables());

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = usePumpsQuery({ variables: queryVariables });

  const allRows = computed(() => {
    let pumps = data.value?.pumps as PumpsRow[] | undefined;

    if (!pumps) {
      return pumps;
    }

    // Apply frequency-adjusted filtering if enabled (reuse same ratio as getVariables)
    if (flowRateFilter.value && targetFlowRate.value) {
      const frequencyRatio = pumpHz.value / 60;

      // Filter 1: Already done in GraphQL query (ROR range)
      // Filter 2: BEP rate adjusted by frequency
      pumps = pumps.filter(pump => {
        const bepRate60Hz = pump.bep_rate_standard_pump_bpd ?? 0;
        const bepRateAdjusted = bepRate60Hz * frequencyRatio;
        // Allow BEP within reasonable range of target (80-120%)
        const bepMin = targetFlowRate.value * 0.8;
        const bepMax = targetFlowRate.value * 1.2;
        return bepRateAdjusted >= bepMin && bepRateAdjusted <= bepMax;
      });

      // Filter 3: OD filtering - pump must fit through casings
      if (topCasingDriftInches.value > 0) {
        pumps = pumps.filter(pump => {
          const pumpOD = pump.housing_outer_diameter_inches ?? 0;
          const fitsTopCasing =
            pumpOD + MIN_CLEARANCE <= topCasingDriftInches.value;

          // If pump extends into bottom casing, must fit both
          if (
            topCasingDepth.value > 0 &&
            pumpDepth.value > topCasingDepth.value &&
            bottomCasingDriftInches.value > 0
          ) {
            const fitsBottomCasing =
              pumpOD + MIN_CLEARANCE <= bottomCasingDriftInches.value;
            return fitsTopCasing && fitsBottomCasing;
          }

          return fitsTopCasing;
        });
      }

      // Sort by closest to BEP (frequency-adjusted) first
      pumps = [...pumps].sort((a, b) => {
        const aBep = (a.bep_rate_standard_pump_bpd ?? 0) * frequencyRatio;
        const bBep = (b.bep_rate_standard_pump_bpd ?? 0) * frequencyRatio;
        const aDiff = Math.abs(aBep - targetFlowRate.value);
        const bDiff = Math.abs(bBep - targetFlowRate.value);
        return aDiff - bDiff;
      });
    }

    // Sort by efficiency after all filters/search are applied
    // Note: Efficiency requires scenario calculations, so this will be enhanced
    // when efficiency data is available. For now, pumps are sorted by BEP proximity.
    // Efficiency sorting will be handled at component level where scenario context exists.

    return pumps;
  });

  const hashOrder: (keyof PumpsRow)[] = ["manufacturer", "series_model"];

  const { defineVisibleColumns, defineFilterData, duplicatedEquipmentId } =
    baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertPump } = useInsertPumpMutation();
  const { executeMutation: executeUpdatePump } = useUpdatePumpByPkMutation();
  const { executeMutation: executeDeletePump } = useDeletePumpsMutation();
  const { executeMutation: executeRestorePump } = useRestorePumpsMutation();

  const insertPump = async (
    savedPump: Omit<Pumps, "housings" | "sizing_import" | "user">
  ) => {
    const duplicatedPump = allRows.value?.find(
      _pump => duplicatedEquipmentId.value === _pump.id
    ) as PumpsQuery["pumps"][number] | undefined;

    const duplicatedPumpId = v4();
    const variables: Pumps_Insert_Input = {
      ...savedPump,
      id: duplicatedPumpId
    };

    if (duplicatedPump) {
      variables.housings = {
        data: duplicatedPump.r_housings.map(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ({ __typename, author_id, pump_id, ...housing }) => ({
            // eslint-disable-line @typescript-eslint/no-unused-vars
            ...housing,
            id: v4(),
            created_at: "now()",
            updated_at: "now()"
          })
        )
      };
    }
    return executeInsertPump({ object: variables });
  };

  const updatePump = (id: string, pump: Pumps) =>
    executeUpdatePump({ id, object: pump });

  const deletePump = (ids: string[]) =>
    executeDeletePump({ where: deletePumps(ids) });

  const restorePump = (ids: string[]) =>
    executeRestorePump({ where: restorePumps(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "pumps",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: null,
    rotation: null,
    part_number: null,
    compression_thrust_per_stage: null,
    series_model: null,
    stage_type: null,
    shaft_diameter_inches: null,
    housing_outer_diameter_inches: null,
    housing_inner_diameter_inches: null,
    neck_outer_diameter_inches: null,
    neck_inner_diameter_inches: null,
    standard_housing_pressure_rating_psi: null,
    high_strength_housing_pressure_rating_psi: null,
    standard_shaft_rating_hp: null,
    high_strength_shaft_rating_hp: null,
    construction: null,
    min_rate_standard_pump_bpd: null,
    bep_rate_standard_pump_bpd: null,
    max_rate_standard_pump_bpd: null,
    max_graph_rate_standard_pump_bpd: null,
    hb: null,
    h1: null,
    h2: null,
    h3: null,
    h4: null,
    h5: null,
    bhpb: null,
    bhp1: null,
    bhp2: null,
    bhp3: null,
    bhp4: null,
    bhp5: null,
    is_compression_pump: false
  }));

  return {
    searchPlaceholder: "Search Pumps...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "SF500"`,
    columns: defineVisibleColumns(pumpTableColumns),
    handleUpsert: defineUpsert(insertPump, updatePump),
    handleDelete: defineDelete(deletePump),
    handleRestore: defineRestore(restorePump),
    totalCount: computed(() => data.value?.pumps_aggregate.aggregate?.count),
    title,
    allRows,
    isAdmin,
    refetch,
    flowRateFilter,
    targetFlowRate,
    pumpHz,
    topCasingDriftInches,
    bottomCasingDriftInches,
    pumpDepth,
    topCasingDepth,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: pumpTableColumns,
      globalSearchFields: ["manufacturer", "series_model"],
      allRows,
      title
    })
  };
}
